import {useCallback, useEffect, useRef} from 'react'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {LOCAL_ROOM_PREFIX} from '#/lib/constants'
import {logger} from '#/logger'
import {useDirectRooms} from '#/state/matrix/hooks/useDirectRooms'
import {useMatrixClient} from '#/state/matrix/hooks/useMatrixClient'
import * as Toast from '#/view/com/util/Toast'
import * as Dialog from '#/components/Dialog'
import {SecureSearchablePeopleList} from '#/components/dialogs/SecureSearchablePeopleList'

const MATRIX_SERVER_NAME = 'chat.batesky.app'

function didToMatrixUserId(did: string): string {
  const localpart = did.replace(/[^a-z0-9]/gi, '_').toLowerCase()
  return `@${localpart}:${MATRIX_SERVER_NAME}`
}

export function NewSecureChat({
  control,
  onNewChat,
}: {
  control: Dialog.DialogControlProps
  onNewChat: (roomId: string) => void
}) {
  const {_} = useLingui()
  const {client} = useMatrixClient()
  const {getExistingDmRoomId} = useDirectRooms()
  const isCreating = useRef(false)

  // Reset guard when dialog opens so it's not stuck from a previous session
  useEffect(() => {
    if (control.isOpen) {
      isCreating.current = false
    }
  }, [control.isOpen])

  const onCreateChat = useCallback(
    (did: string) => {
      if (!client || isCreating.current) return

      try {
        const matrixUserId = didToMatrixUserId(did)

        // Check for existing DM room with this user
        const existingRoomId = getExistingDmRoomId(matrixUserId)
        if (existingRoomId) {
          onNewChat(existingRoomId)
          control.close()
          return
        }

        // Navigate to a local room immediately (no server call)
        isCreating.current = true
        const localRoomId = `${LOCAL_ROOM_PREFIX}${matrixUserId}`
        onNewChat(localRoomId)
        control.close()
      } catch (error) {
        isCreating.current = false
        logger.error('Failed to start secure chat', {safeMessage: error})
        Toast.show(_(msg`An issue occurred starting the secure chat`), 'xmark')
      }
    },
    [control, client, onNewChat, _, getExistingDmRoomId],
  )

  return (
    <Dialog.Outer control={control} testID="newSecureChatDialog">
      <Dialog.Handle />
      <SecureSearchablePeopleList
        title={_(msg`Start a new secure chat`)}
        onSelectChat={onCreateChat}
      />
    </Dialog.Outer>
  )
}
